import { useMemo, useState } from 'react';
import type { EntityDirectoryHit } from '@hero/api';
import type { Client, Party } from '@hero/schema';
import { getObjectId, makePrimaryIdEntry, makeIdEntry } from '@hero/schema';
import { useClientLookupByLinkApi } from '@hero/api-hooks';

/**
 * Build a `Party` snapshot suitable for `Client.linkedParty` from
 * either a directory hit or a freshly suggested Profile.
 *
 * Picker convention (single rule across the picker contract):
 * `target: 'profile'` wins over `target: 'org'` when the hit carries
 * both ids. The Profile is the deduplication anchor and the most
 * stable identity even after signup; the bridged `org_id` is
 * preserved in `ids[]` so downstream Route / Order writes can match
 * the Org without a join.
 */
export function hitToParty(hit: EntityDirectoryHit): Party {
  if (hit.target === 'profile') {
    if (!hit.profileId) {
      throw new Error('EntityDirectoryHit with target=profile is missing profileId');
    }
    const profileObjectId = getObjectId(hit.profileId);
    return {
      _id: profileObjectId,
      name: hit.name,
      target: 'profile',
      roles: ['business_customer'],
      ids: [
        makePrimaryIdEntry({ _id: profileObjectId } as never, 'profile'),
        ...(hit.orgId ? [makeIdEntry('org_id', hit.orgId)] : []),
      ],
    };
  }
  if (!hit.orgId) {
    throw new Error('EntityDirectoryHit with target=org is missing orgId');
  }
  const orgObjectId = getObjectId(hit.orgId);
  return {
    _id: orgObjectId,
    name: hit.name,
    target: 'org',
    roles: ['business_customer'],
    ids: [makePrimaryIdEntry({ _id: orgObjectId } as never, 'org')],
  };
}

/**
 * Manager hook for the unified picker inside the Client-create
 * flow. Drives:
 *
 * - Picker visibility (`isPickerOpen`).
 * - The current selection (`pickedHit` or `createdProfile`).
 * - The `lookup-by-link` dedup probe — when the caller's Org already
 *   has a Client linked to the picked identity, the consumer should
 *   surface an "Open existing Client" deep link instead of letting
 *   the user POST a duplicate.
 *
 * Consumers convert the selection to a `Party` snapshot via
 * {@link hitToParty} and feed it into `useClientForm`
 * (`form.setValue('linkedParty', party)`).
 */
export function useClientLinkPickerManager() {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickedHit, setPickedHit] = useState<EntityDirectoryHit | undefined>(undefined);

  const openPicker = () => setIsPickerOpen(true);
  const closePicker = () => setIsPickerOpen(false);

  const lookup = useClientLookupByLinkApi({
    profileId: pickedHit?.profileId ? getObjectId(pickedHit.profileId) : undefined,
    orgId: pickedHit?.orgId ? getObjectId(pickedHit.orgId) : undefined,
  });

  const existingClient = (lookup.data ?? null) as Client | null;
  const isCheckingDupe = lookup.isFetching;

  const reset = () => {
    setPickedHit(undefined);
    setIsPickerOpen(false);
  };

  const pickedParty = useMemo<Party | undefined>(
    () => (pickedHit ? hitToParty(pickedHit) : undefined),
    [pickedHit],
  );

  return {
    isPickerOpen,
    openPicker,
    closePicker,
    pickedHit,
    setPickedHit,
    pickedParty,
    existingClient,
    isCheckingDupe,
    reset,
  };
}
